/**
 * Notification Initializer Service
 * 
 * Initializes push notifications and sets up notification infrastructure
 */

import { OneSignalService } from './OneSignalService';

export class NotificationInitializer {
  private static instance: NotificationInitializer;
  private isInitialized = false;
  private oneSignalService = OneSignalService.getInstance();

  static getInstance(): NotificationInitializer {
    if (!NotificationInitializer.instance) {
      NotificationInitializer.instance = new NotificationInitializer();
    }
    return NotificationInitializer.instance;
  }

  /**
   * Initialize notifications for the current user
   */
  async initialize(userId: string): Promise<void> {
    if (this.isInitialized) {
      console.log('📱 Notifications already initialized');
      return;
    }

    try {
      console.log('📱 Initializing notifications for user:', userId);

      await this.oneSignalService.initialize();
      await this.oneSignalService.login(userId);

      const permission = await this.oneSignalService.requestPermission();
      if (permission !== 'granted') {
        console.warn('📱 Notification permission not granted:', permission);
      } else {
        console.log('📱 Push notifications initialized successfully with OneSignal');
      }

      // Set up notification event listeners
      this.setupEventListeners();

      this.isInitialized = true;
      console.log('📱 Notification initialization complete');

    } catch (error) {
      console.error('📱 Failed to initialize notifications:', error);
    }
  }

  /**
   * Setup event listeners for notifications
   */
  private setupEventListeners(): void {
    this.oneSignalService.onForegroundNotification((event) => {
      try {
        const notification = event?.notification ?? event;
        const additionalData = event?.data?.additionalData ?? notification?.additionalData ?? {};
        const detail = {
          id: additionalData.notificationId || Date.now().toString(),
          type: additionalData.type || 'system_alert',
          title: notification?.title || 'Notification',
          message: notification?.body || '',
          priority: additionalData.priority || 'normal',
          data: additionalData,
        };

        const customEvent = new CustomEvent('newNotification', { detail });
        window.dispatchEvent(customEvent);
      } catch (error) {
        console.warn('📱 Failed to dispatch foreground notification event:', error);
      }
    });

    // Listen for new notifications from service worker
    window.addEventListener('newNotification', (event: any) => {
      console.log('📱 New notification received:', event.detail);
      // The notification center will handle this automatically
    });

    // Handle notification clicks via OneSignal callbacks
    this.oneSignalService.onNotificationClick((event) => {
      this.handleNotificationClick(event);
    });
  }

  /**
   * Handle notification click
   */
  private handleNotificationClick(data: any): void {
    console.log('📱 Handling notification click:', data);
    
    const additionalData = data?.notification?.additionalData || data?.data || {};
    const targetUrl = additionalData.deepLink || data?.url;

    if (targetUrl) {
      // Navigate to the specified URL
      window.location.href = targetUrl;
    }
  }

  /**
   * Reset initialization state (for logout)
   */
  reset(): void {
    this.isInitialized = false;
    this.oneSignalService.logout().catch((error) => {
      console.warn('📱 Failed to logout from OneSignal:', error);
    });
    console.log('📱 Notification initialization reset');
  }

  /**
   * Send test notification
   */
  async sendTestNotification(userId: string): Promise<void> {
    try {
      console.log('📱 Trigger send test notification for user:', userId);
      // Test notifications should be sent via backend (OneSignal dashboard or Cloud Function)
      // Keeping this method for backwards compatibility.
    } catch (error) {
      console.error('📱 Failed to send test notification:', error);
      throw error;
    }
  }
}
